<script setup>
import { inventoryQueryClientRequest } from '@/api/inventory'
import { createReusableTemplate } from '@vueuse/core'
import { computed, getCurrentInstance, ref, onMounted, onUnmounted } from 'vue'
import { Route } from 'vue-router'
import { RouterLink } from 'vue-router'
import { useRoute, useRouter } from 'vue-router/composables'
import { i18n } from '@/i18n'
import { useStore } from '@/store'
import ModalEditReservedStock from './ModalEditReservedStock.vue'
import DrawerReservedStockHistory from './DrawerReservedStockHistory.vue'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  warehouseList: {
    type: Array,
    default: () => [],
  },
  isChild: {
    type: Boolean,
    default: false,
  },
  index: {
    type: Number,
    default: -1,
  },
})

const alt = ref(false)
const shift = ref(false)

function handleKeydown(e) {
  if (e.altKey) alt.value = true
  if (e.shiftKey) shift.value = true
}

function handleKeyup(e) {
  if (!e.altKey) alt.value = false
  if (!e.shiftKey) shift.value = false
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('keyup', handleKeyup)
  window.addEventListener('blur', () => {
    alt.value = false
    shift.value = false
  })
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('keyup', handleKeyup)
})

const showOriginalValue = computed(() => alt.value && shift.value)

const router = useRouter()
const route = useRoute()
const store = useStore()
const { $notification } = getCurrentInstance().proxy

const warehouse = computed(
  () =>
    props.warehouseList.find(wh => wh.id === props.item.warehouse_id) || {},
)

const permission = computed(() => store.getters['user/can'](route.meta?.key ?? 'list-inventoryv2'))
const canEditReserved = computed(() => {
  const keys = [route.meta?.key ?? 'list-inventoryv2', 'stock-alocation']
  return keys.some(key => {
    const perms = store.getters['user/can'](key)
    if (Array.isArray(perms)) return perms.includes('WRITE')
    if (typeof perms === 'string') return perms.includes('WRITE')
    return false
  })
})

const [DefineTableItemTemplate, TableItem] = createReusableTemplate()

function statusLabel(stock) {
  const value = +stock

  if (value > 0) {
    return 'IN_STOCK'
  }

  return 'OUT_OF_STOCK'
}

function finalComponentAvailability(item) {
  if (item.availability < 0) {
    return 0
  }

  return Math.floor(item.availability / item.component_quantity)
}

function isSoftBundling(item) {
  return item.bundling_type === 'SOFT'
}

// ---------------------------------------------------------------------------
// Reserved Stock
// ---------------------------------------------------------------------------

const reservedStock = ref(Number(props.item.reserved_stock) || 0)

/**
 * Computed on_order = on_hand - availability - reserved_stock
 * on_order is NOT stored in DB, it's derived
 */
function computedOnOrder(item) {
  const onHand = Number(item.on_hand) || 0
  const availability = Number(item.availability) || 0
  const reserved = reservedStock.value
  const result = onHand - availability - reserved
  return result
}

// Modal: Edit Reserved Stock
const showEditModal = ref(false)
const editModalItem = ref({})

function openEditModal(item) {
  editModalItem.value = item
  showEditModal.value = true
}

function onEditSuccess({ reserved_stock, new_availability }) {
  reservedStock.value = reserved_stock
  // Directly update availability on the item so UI reflects immediately
  if (Number.isFinite(new_availability)) {
    props.item.availability = new_availability
  }
  showEditModal.value = false
  $notification.success({
    message: i18n.t('productAvailability.reserved_stock_updated'),
  })
}

// Drawer: Reserved Stock History
const showHistoryDrawer = ref(false)
const historyDrawerItem = ref({})

function openHistoryDrawer(item) {
  historyDrawerItem.value = {
    ...item,
    warehouse_name: warehouse.value.name,
  }
  showHistoryDrawer.value = true
}

// ---------------------------------------------------------------------------
// Soft bundling children
// ---------------------------------------------------------------------------

const isOpen = ref(false)
const fetchingChildren = ref(false)
const children = ref([])
async function fetchChildren() {
  fetchingChildren.value = true

  try {
    const { data } = await inventoryQueryClientRequest({
      method: 'GET',
      url: `/components/${props.item.product_id}`,
      params: {
        warehouse_id: props.item.warehouse_id,
      },
      headers: {
        'Business-Id': props.item.business_id,
      },
    })

    children.value = data.data || []
  } catch (err) {
    alert(i18n.t('productAvailability.error_fetch_soft_bundling'))
    isOpen.value = false
  } finally {
    fetchingChildren.value = false
  }
}

function toggleChildrenWrapper() {
  isOpen.value = !isOpen.value

  if (isOpen.value) {
    fetchChildren()
  }
}

function detailRoute(item) {
  return {
    path: `/inventory/movement/${item.catalog_id}`,
    query: {
      business_id: item.business_id,
      warehouse_id: item.warehouse_id,
      workspace_id: route.query.workspace_id,
      product_title: item.title,
      product_sku: item.sku,
    },
  }
}

/**
 * Get route for allocation
 * 
 * @param item 
 * 
 * @returns {Route}
 */
function allocationRoute(item) {
  return {
    name: 'product.allocation',
    params: {
      id: item.product_id,
    },
    query: {
      business_id: route.query.business_id,
      workspace_id: route.query.workspace_id,
      warehouse_id: item.warehouse_id,
    },
  }
}

function onClickDetail(item) {
  if (isSoftBundling(item)) {
    return toggleChildrenWrapper()
  }

  router.push(detailRoute(item))
}

function copyToClipboard(text) {
  if (!text) return
  navigator.clipboard.writeText(text).then(() => {
    $notification.success({
      message: i18n.t('productAvailability.copy_success', { value: text }) || `Copied ${text}`,
    })
  })
}
</script>

<template>
  <tbody
    :class="{
      'is-open': isOpen,
    }"
  >
    <DefineTableItemTemplate v-slot="{ value, isChild }">
      <tr
        :class="{
          'is-child-item': isChild,
          'is-soft-bundling': isSoftBundling(value),
          [statusLabel(value.availability)]: true,
        }"
      >
        <td :colspan="isChild ? 2 : 1">
          <div class="d-flex">
            <div>
              <div
                class="d-flex align-items-center"
                :class="{ 'mb-1': isSoftBundling(value) }"
              >
                <div class="sku-wrapper">
                  <div class="sku">
                    {{ value.sku }}
                  </div>
                  <a-icon type="copy" class="sku-copy-btn" @click="copyToClipboard(value.sku)" />
                </div>

                <div v-if="isSoftBundling(value)" class="soft-bundling-tag">
                  {{ $t('productAvailability.soft_bundling') }}
                </div>
              </div>

              <a class="product-title" @click.prevent="onClickDetail(value)">
                {{ value.title }}
              </a>
            </div>

            <div v-if="isChild" class="component-quantity">
              x{{ value.component_quantity }}
            </div>
          </div>
        </td>
        <td v-if="!isChild">
          <div class="small text-muted">
            {{ value.business_name }}
          </div>
          <div class="warehouse-name">
            {{ warehouse.name }}
          </div>
        </td>
        <td class="inventory-list-item__stock stock-on-hand">
          <div class="stock-cell-wrapper">
            <template v-if="isSoftBundling(value)">
              <a-tooltip :title="$t('productAvailability.soft_bundling_tooltip')">
                <span class="text-muted">-</span>
              </a-tooltip>
            </template>
            <template v-else>
              <a-tooltip v-if="showOriginalValue && !isSoftBundling(value) && value.on_hand < 0" :title="`Original Value: ${value.on_hand}`">
                <span style="color: #ff4d4f; font-weight: bold;">{{ value.on_hand }}</span>
              </a-tooltip>
              <span v-else>{{ value.on_hand < 0 ? 0 : value.on_hand }}</span>
            </template>
            <div class="stock-icon-area" />
          </div>
        </td>
        <td class="inventory-list-item__stock stock-reserved">
          <div class="stock-cell-wrapper">
            <template v-if="isSoftBundling(value) && !isChild">
              <a-tooltip :title="$t('productAvailability.reserved_stock_tooltip')">
                <span class="text-muted">-</span>
              </a-tooltip>
            </template>
            <span v-else class="reserved-stock-value">
              {{ isChild ? (Number(value.reserved_stock) || 0) : reservedStock }}
            </span>
            
            <div class="stock-icon-area">
              <a
                v-if="!isSoftBundling(value) && !isChild && canEditReserved"
                class="reserved-stock-edit"
                @click.prevent="openEditModal(value)"
              >
                <a-icon type="edit" />
              </a>
            </div>
          </div>
        </td>
        <td class="inventory-list-item__stock stock-on-order">
          <div class="stock-cell-wrapper">
            <a-tooltip
              v-if="isSoftBundling(value)"
              :title="$t('productAvailability.soft_bundling_tooltip_order')"
            >
              <span class="text-muted"> - </span>
            </a-tooltip>
            <span v-else>
              {{ isChild ? value.in_order : computedOnOrder(value) }}
            </span>
            <div class="stock-icon-placeholder" />
          </div>
        </td>
        <td class="inventory-list-item__stock stock-available">
          <div class="stock-cell-wrapper">
            <span class="availability">{{ value.availability }}</span>
            <div class="stock-icon-area" />
          </div>
          <div
            v-if="isChild && value.component_quantity > 1"
            class="stock-cell-wrapper"
          >
            <span class="final-component-availability">
              [{{ finalComponentAvailability(value) }}]
            </span>
          </div>
        </td>
        <td class="text-center">
          {{ value.uom || value.unit }}
        </td>
        <td class="text-center" :class="statusLabel(value.availability)">
          <div class="d-flex justify-content-center w-100">
            <a-tag v-if="value.availability !== 'NONE'" class="m-0">
              {{ $t(`productAvailability.${statusLabel(value.availability)}`) }}
            </a-tag>
          </div>
        </td>
        <td class="text-right">
          <a-dropdown placement="bottomRight">
            <a @click.prevent>
              <a-icon type="more" />
            </a>

            <template #overlay>
              <a-menu>
                <a-menu-item v-if="isSoftBundling(value)">
                  <a @click.prevent="onClickDetail(value)">
                    <a-icon type="ordered-list" class="mr-2" />
                    <span>
                      {{
                        isOpen
                          ? $t('productAvailability.hide_bundle_components')
                          : $t('productAvailability.show_bundle_components')
                      }}
                    </span>
                  </a>
                </a-menu-item>

                <a-menu-item v-else>
                  <RouterLink :to="detailRoute(value)">
                    <a-icon type="swap" class="mr-2" />
                    <span>{{ $t('productAvailability.movement_transaction') }}</span>
                  </RouterLink>
                </a-menu-item>

                <a-menu-item v-if="!isSoftBundling(value) && !isChild">
                  <a @click.prevent="openHistoryDrawer(value)">
                    <a-icon type="history" class="mr-2" />
                    <span>{{ $t('productAvailability.reserved_stock_history') }}</span>
                  </a>
                </a-menu-item>

                <a-divider style="margin: 8px 0" />

                <a-menu-item>
                  <router-link
                    :to="{
                      path: '/product/edit',
                      query: {
                        business_id: value.business_id,
                        id: value.product_id,
                        workspace_id: route.query.workspace_id,
                      },
                    }"
                  >
                    <a-icon type="edit" class="mr-2" />
                    <span>{{ $t('productAvailability.product_detail') }}</span>
                  </router-link>
                </a-menu-item>

                <a-menu-item>
                  <router-link :to="allocationRoute(value)">
                    <a-icon type="container" class="mr-2" />
                    <span>{{ $t('product.stock_allocation') }}</span>
                  </router-link>
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </td>
      </tr>
    </DefineTableItemTemplate>

    <TableItem
      :key="`${props.item.catalog_id}__${props.item.warehouse_id}`"
      :value="props.item"
      :is-child="props.isChild"
    />

    <template v-if="isSoftBundling(props.item) && isOpen">
      <template v-if="fetchingChildren && !children.length">
        <tr v-for="i in 3" :key="i">
          <td colspan="9">
            <a-skeleton
              active
              :paragraph="false"
              :avatar="false"
              :loading="true"
            />
          </td>
        </tr>
      </template>

      <template v-for="child in children">
        <TableItem
          :key="`${props.item.catalog_id}__${props.item.warehouse_id}__${child.catalog_id}__${child.warehouse_id}`"
          :value="child"
          :is-child="true"
        />
      </template>
    </template>

    <!-- Reserved Stock Edit Modal -->
    <ModalEditReservedStock
      :visible="showEditModal"
      :item="editModalItem"
      :current-reserved="reservedStock"
      :warehouse-name="warehouse.name || ''"
      @close="showEditModal = false"
      @success="onEditSuccess"
    />

    <!-- Reserved Stock History Drawer -->
    <DrawerReservedStockHistory
      :visible="showHistoryDrawer"
      :item="historyDrawerItem"
      @close="showHistoryDrawer = false"
    />
  </tbody>
</template>

<style lang="scss" scoped>
.ant-skeleton::v-deep {
  .ant-skeleton-title {
    margin: 0;
  }
}
.sku-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    .sku-copy-btn {
      opacity: 1;
    }
  }
}

.sku {
  font-size: 12px;
  color: #444444;
  letter-spacing: 0.025rem;
  font-family: monospace;
}

.sku-copy-btn {
  font-size: 11px;
  color: #bfbfbf;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s;

  &:hover {
    color: #1890ff;
  }
}

.soft-bundling-tag {
  font-size: 10px;
  border: 1px solid rgba(0, 123, 255, 0.8);
  border-radius: 2px;
  padding: 0.5px 4px;
  margin-left: 0.5rem;
  color: rgba(0, 123, 255, 0.8);
  white-space: nowrap;
}

.product-title {
  color: var(--kit-color-primary);
  font-weight: 500;
  font-size: 95%;
}

.component-quantity {
  font-size: 90%;
  margin-left: auto;
  padding-left: 1rem;
  color: var(--secondary);
  font-family: monospace;
  letter-spacing: 1px;
}

.warehouse-name {
  font-size: 95%;
  white-space: nowrap;
}

.availability {
  // font-weight: 500;
  // color: #005a18;
}

.final-component-availability {
  font-weight: normal;
  color: var(--secondary);
  display: block;
  font-size: 85%;
}

.inventory-list-item__stock {
  padding: 12px 8px;
}

.stock-cell-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding-right: 32px !important;
  position: relative;
}

.stock-icon-area {
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: absolute;
  right: 0;
}

.stock-on-hand,
.stock-reserved,
.stock-on-order,
.stock-available {
  font-weight: 500;
  color: #262626;
}

.stock-reserved {
  .reserved-stock-value {
    color: inherit;
  }
}

.ant-tag {
  font-size: 10px;
  padding: 2px 6px;
  line-height: 1.5;
  border-radius: 2px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.IN_STOCK {
  .ant-tag {
    color: #1890ff;
    background: #e6f7ff;
    border-color: #91d5ff;
  }
}

.OUT_OF_STOCK {
  .ant-tag {
    color: #fa8c16;
    background: #fff7e6;
    border-color: #ffd591;
  }

  .availability {
    // color: var(--warning);
  }
}

.MINUS_OF_STOCK {
  .ant-tag {
    color: #f5222d;
    background: #fff1f0;
    border-color: #ffa39e;
  }
}

.is-child-item {
  td:first-child {
    padding-left: 4rem;
    position: relative;

    &::before {
      content: '';
      top: 0;
      bottom: 0;
      width: 1px;
      position: absolute;
      left: 1.5rem;
      background-color: rgba(0, 0, 0, 0.1);
    }

    &::after {
      content: '';
      top: 50%;
      width: 20px;
      height: 1px;
      position: absolute;
      left: 1.5rem;
      background-color: rgba(0, 0, 0, 0.1);
    }
  }
}

.is-open td {
  background-color: #f9fcff;
}

.is-open tr:last-child td {
  border-bottom: 6px solid #cfd5db;
}

.reserved-stock-edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 3px;
  color: #999;
  font-size: 12px;
  transition: all 0.2s;

  &:hover {
    background-color: #fff1f0;
    color: #ff4d4f;
  }
}
</style>
